import React, { useEffect, useState } from "react";
import { PageHero } from "../components/Sections";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { partnersMeta } from "../mock/data";
import { Info, ShieldCheck, Building2, Globe, ImageOff } from "lucide-react";

function PartnerCard({ p, meta }) {
  return (
    <Card className="flex h-full flex-col overflow-hidden border">
      <div className="flex items-center gap-4 border-b bg-secondary/30 p-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-border">
          {p.logo ? <img src={p.logo} alt={p.name} className="h-full w-full object-contain" />
            : <Building2 className="h-6 w-6 text-primary" />}
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-base font-semibold leading-tight">{p.name || "(Unnamed)"}</h3>
          {p.entity_type && <span className="text-xs text-muted-foreground">{p.entity_type}</span>}
        </div>
      </div>
      <div className="flex-1 space-y-2.5 p-4 text-sm">
        {meta.showReg && p.registration && (
          <div><span className="font-medium">{meta.regLabel}: </span><span className="text-muted-foreground">{p.registration}</span></div>
        )}
        {p.products && (
          <div><span className="font-medium">{meta.productsLabel}: </span><span className="text-muted-foreground">{p.products}</span></div>
        )}
        {meta.showRole && p.role && (
          <div><span className="font-medium">Our Role: </span><span className="text-muted-foreground">{p.role}</span></div>
        )}
        {meta.showGrievance && p.grievance && (
          <div><span className="font-medium">Grievance Escalation: </span><span className="text-muted-foreground">{p.grievance}</span></div>
        )}
        {p.website && (
          <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-primary hover:underline">
            <Globe className="h-3.5 w-3.5" /> Visit website
          </a>
        )}
      </div>
    </Card>
  );
}

export default function Partners({ type = "lending" }) {
  const meta = partnersMeta[type] || partnersMeta.lending;
  const [partners, setPartners] = useState(null);

  useEffect(() => {
    let active = true;
    setPartners(null);
    api.get(`/partners?kind=${type}`)
      .then(({ data }) => { if (active) setPartners(data); })
      .catch(() => { if (active) setPartners([]); });
    return () => { active = false; };
  }, [type]);

  return (
    <div>
      <PageHero title={`Our ${meta.title}`} subtitle={meta.intro} crumbs={[{ label: meta.title }]} />
      <section className="section-pad">
        <div className="container-page">
          <div className="mb-8 flex items-start gap-3 rounded-xl border border-primary/20 bg-secondary/50 p-4">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
            <p className="text-sm text-muted-foreground"><strong className="text-foreground">Note: </strong>{meta.role}</p>
          </div>

          {partners === null ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(3)].map((_, i) => <div key={i} className="h-48 animate-pulse rounded-xl bg-secondary" />)}
            </div>
          ) : partners.length === 0 ? (
            <Card className="border p-10 text-center">
              <ImageOff className="mx-auto mb-3 h-10 w-10 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground">
                Verified {meta.title.toLowerCase()} will be listed here. We do not display logos merely to appear larger —
                only onboarded, verified entities are shown with accurate details.
              </p>
            </Card>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {partners.map((p) => <PartnerCard key={p.id} p={p} meta={meta} />)}
            </div>
          )}

          <div className="mt-8 flex items-start gap-3 rounded-xl border bg-card p-4">
            <Info className="h-5 w-5 shrink-0 text-primary" />
            <p className="text-sm text-muted-foreground">
              Partner details are managed and verified before publishing. Registration information is shown where
              applicable.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
